/**
 * AdminPermissionsPage — RBAC role and permission management.
 *
 * Renders dynamically from src/config/permissions.ts (SSOT).
 * Zero hardcoded role names or permission strings in this file.
 */

import React from 'react';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModuleCard } from '../../shared/components/ModulePageHeader';
import {
  SYSTEM_ROLES,
  APP_MODULES,
  PERMISSION_MATRIX,
  canDo,
  type ModuleAction,
} from '../../../config/permissions';

const ACTION_LABELS: Record<ModuleAction, string> = {
  view:   'Visualizar',
  create: 'Criar',
  edit:   'Editar',
  delete: 'Excluir',
};

const ALL_ACTIONS: ModuleAction[] = ['view', 'create', 'edit', 'delete'];

export const AdminPermissionsPage: React.FC = () => {
  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-page h-full overflow-y-auto">

          {/* ── Role Cards ─────────────────────────────────────── */}
          <ModuleCard title="Papéis do Sistema">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {SYSTEM_ROLES.map((role) => {
                const totalGranted = APP_MODULES.reduce(
                  (acc, mod) => acc + PERMISSION_MATRIX[role.key][mod.key].length,
                  0,
                );
                return (
                  <div
                    key={role.key}
                    className={`enterprise-card-surface p-3 rounded border-l-2 ${role.color}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[12px] font-semibold enterprise-text-primary">{role.label}</span>
                      <span className="text-[10px] font-mono enterprise-text-secondary bg-enterprise-border-soft px-1.5 py-0.5 rounded">
                        {role.key}
                      </span>
                    </div>
                    <p className="text-[11px] enterprise-text-secondary">{role.description}</p>
                    <p className="mt-2 text-[11px] enterprise-text-secondary">
                      <span className="font-medium">{totalGranted}</span> permissões
                    </p>
                  </div>
                );
              })}
            </div>
          </ModuleCard>

          {/* ── Permission Matrix Table ─────────────────────────── */}
          <ModuleCard title="Matriz de Permissões por Módulo">
            <div className="enterprise-table-shell overflow-x-auto">
              <table className="w-full text-left text-[12px]">
                <thead className="enterprise-table-header">
                  <tr>
                    <th className="enterprise-table-head-cell" style={{ minWidth: 180 }}>Módulo / Ação</th>
                    {SYSTEM_ROLES.map((role) => (
                      <th
                        key={role.key}
                        className="enterprise-table-head-cell text-center"
                        style={{ minWidth: 88 }}
                      >
                        {role.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {APP_MODULES.map((mod, modIdx) =>
                    ALL_ACTIONS.map((action, actionIdx) => {
                      const isFirstRow = actionIdx === 0;
                      return (
                        <tr
                          key={`${mod.key}-${action}`}
                          className={`enterprise-table-row ${modIdx % 2 === 0 ? '' : 'bg-[var(--bg-surface-alt,transparent)]'}`}
                        >
                          <td className="enterprise-table-cell">
                            {isFirstRow ? (
                              <div>
                                <span className="font-semibold enterprise-text-primary">{mod.label}</span>
                                <span className="block text-[10px] font-normal enterprise-text-secondary">
                                  {ACTION_LABELS[action]}
                                </span>
                              </div>
                            ) : (
                              <span className="pl-3 text-[11px] enterprise-text-secondary">
                                {ACTION_LABELS[action]}
                              </span>
                            )}
                          </td>
                          {SYSTEM_ROLES.map((role) => {
                            const granted = canDo(role.key, action, mod.key);
                            return (
                              <td key={role.key} className="enterprise-table-cell text-center">
                                <span
                                  className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold mx-auto ${
                                    granted
                                      ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400'
                                      : 'bg-slate-100 text-slate-400 dark:bg-zinc-800 dark:text-zinc-600'
                                  }`}
                                >
                                  {granted ? '✓' : '–'}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </ModuleCard>

          <div className="enterprise-card-surface p-3 text-[11px] enterprise-text-secondary border-l-2 border-amber-400">
            A matriz acima representa as permissões padrão do sistema (SSOT em <code className="font-mono">src/config/permissions.ts</code>).
            Configurações por organização são gerenciadas via tabelas <code className="font-mono">roles</code>, <code className="font-mono">role_permissions</code> e <code className="font-mono">user_roles</code>.
          </div>

          {/* ── Data Scope ─────────────────────────────────────── */}
          <ModuleCard title="Visibilidade de Dados (Data Scope)">
            <div className="enterprise-card-surface p-3 text-[11px] enterprise-text-secondary border-l-2 border-blue-400 mb-3">
              A visibilidade de dados é automática com base na posição hierárquica do usuário na organização
              (<code className="font-mono">user_organizations.role_type</code>).
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {([
                { position: 'Analista',     scope: 'individual',  label: 'Apenas suas tarefas',              icon: '👤', color: 'border-slate-400' },
                { position: 'Coordenador',  scope: 'team',        label: 'Sua equipe (subordinados diretos)', icon: '👥', color: 'border-blue-400' },
                { position: 'Gerente',      scope: 'department',  label: 'Seu departamento',                 icon: '🏢', color: 'border-amber-400' },
                { position: 'Diretor',      scope: 'global',      label: 'Toda a organização',               icon: '🏛️', color: 'border-emerald-400' },
              ] as const).map((item) => (
                <div key={item.position} className={`enterprise-card-surface p-3 rounded border-l-2 ${item.color}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[14px]">{item.icon}</span>
                    <span className="text-[12px] font-semibold enterprise-text-primary">{item.position}</span>
                    <span className="text-[10px] font-mono enterprise-text-secondary bg-enterprise-border-soft px-1.5 py-0.5 rounded ml-auto">
                      {item.scope}
                    </span>
                  </div>
                  <p className="text-[11px] enterprise-text-secondary">{item.label}</p>
                </div>
              ))}
            </div>
          </ModuleCard>

        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
